/**
 * AddTalentModal — on-brand CV-upload modal for the Talent Pool.
 *
 * Mirrors AddCvModal's chrome and the shared FileUpload widget (the canonical
 * resume-upload component), but uploads to the resume bank with no job via
 * useUploadResumesToBank — uploaded CVs are promoted into the tenant talent pool
 * on the backend. Title reuses pool.addTalent; all other copy reuses the existing
 * on-brand job:add_cv_modal / common keys. No hardcoded strings, no gradients.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { useUploadResumesToBank } from '@/hooks/useJobOffers';
import { FileUpload, CV_SUPPORTED_EXTENSIONS } from '@/components/ui/FileUpload';
import { Button } from '@/components/ui/atoms/Button';

interface AddTalentModalProps {
  onClose: () => void;
}

export const AddTalentModal: React.FC<AddTalentModalProps> = ({ onClose }) => {
  const { t } = useTranslation(['talentPool', 'job', 'common']);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const uploadMutation = useUploadResumesToBank();

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !uploadMutation.isPending) {
      onClose();
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error(t('job:add_cv_modal.toast_select_required'));
      return;
    }

    try {
      const uploadMessage =
        selectedFiles.length === 1
          ? t('job:add_cv_modal.toast_uploading_one')
          : t('job:add_cv_modal.toast_uploading_other', { count: selectedFiles.length });
      const uploadToastId = toast.loading(uploadMessage);

      await uploadMutation.mutateAsync(selectedFiles);

      toast.dismiss(uploadToastId);
      // App.tsx dismisses this fixed id when the batch-complete notification lands.
      toast.loading(t('job:toasts.cv_processing'), {
        id: 'resume-bank-processing',
        duration: Infinity,
      });
      onClose();
    } catch (error) {
      console.error('[TALENT POOL] Upload error:', error);
      toast.error(t('job:add_cv_modal.toast_failed'));
    }
  };

  const fileCount = selectedFiles.length;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h5>{t('pool.addTalent')}</h5>
          <Button
            type="icon"
            intent="muted"
            onClick={onClose}
            disabled={uploadMutation.isPending}
            icon={<X />}
            label={t('common:actions.close')}
          />
        </div>

        <div className="mb-6">
          <FileUpload
            onFilesSelected={handleFilesSelected}
            selectedFiles={selectedFiles}
            onFileRemove={removeFile}
            acceptedFormats={CV_SUPPORTED_EXTENSIONS}
            multiple
            variant="side-by-side"
            iconColor="text-dark-invirtus"
            buttonColor="bg-dark-invirtus hover:bg-dark-invirtus/80"
            buttonTextColor="text-white"
            buttonText={t('job:add_cv_modal.upload_button')}
            description={t('job:add_cv_modal.description')}
            dragHint={t('common:file_upload.drag_hint')}
            selectedFilesTitle={t('job:add_cv_modal.selected_files', { count: fileCount })}
            id="talent-cv-drop-area"
            showFileList
            disabled={uploadMutation.isPending}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            label={t('common:actions.cancel')}
            type="outlined"
            intent="secondary"
            onClick={onClose}
            disabled={uploadMutation.isPending}
          />
          <Button
            label={
              uploadMutation.isPending
                ? t('job:add_cv_modal.uploading')
                : t('job:add_cv_modal.upload_count', { count: fileCount })
            }
            type="contained"
            intent="secondary"
            onClick={handleUpload}
            disabled={uploadMutation.isPending || fileCount === 0}
          />
        </div>
      </div>
    </div>
  );
};

AddTalentModal.displayName = 'AddTalentModal';
